import { companySelectControl } from './companySelect';
import { dataSourceControl } from './dataSource';
import { gameEngineControl } from './gameEngine';
import { gameSelectControl } from './gameSelect';
import { genreSelectControl } from './genreSelect';
import { multipleSelectControl } from './multipleSelect';
import { numberRangeControl } from './numberRange';
import { platformControl } from './platform';
import { publisherClassSelectControl } from './publisherClassSelect';
import { rangePickerControl } from './rangePicker';
import { regionSelectControl } from './regionSelect';
import { revenueGrowthControl } from './revenueGrowth';
import { revenueRangeControl } from './revenueRange';
import { sensorTowerThemeControl } from './sensorTowerTheme';
import { tagSelectControl } from './tagSelect';
import RangePickerMiddlewareUtils from './rangePicker/middlewareUtils';
import RegionSelectMiddlewareUtils from './regionSelect/middlewareUtils';
import CompanySelectMiddlewareUtils from './companySelect/middlewareUtils';
import DataSourceMiddlewareUtils from './dataSource/middlewareUtils';
import GameEngineMiddlewareUtils from './gameEngine/middlewareUtils';
import GameSelectMiddlewareUtils from './gameSelect/middlewareUtils';
import GenreSelectMiddlewareUtils from './genreSelect/middlewareUtils';
import MultipleSelectMiddlewareUtils from './multipleSelect/middlewareUtils';
import NumberRangeFilterMiddlewareUtils from './numberRange/middlewareUtils';
import PlatformMiddlewareUtils from './platform/middlewareUtils';
import PublisherClassSelectMiddlewareUtils from './publisherClassSelect/middlewareUtils';
import RevenueGrowthMiddlewareUtils from './revenueGrowth/middlewareUtils';
import RevenueRangeMiddlewareUtils from './revenueRange/middlewareUtils';
import SensorTowerThemeMiddlewareUtils from './sensorTowerTheme/middlewareUtils';
import TagSelectMiddlewareUtils from './tagSelect/middlewareUtils';
import {
  getNumberRangeValue,
  normalizeLeafStringArrayByItemOptionsKeepEmpty,
  normalizeStringArrayByItemOptions,
  normalizeStringArrayByItemOptionsKeepEmpty,
} from './shared/valueUtils';
import { normalizeRangePickerValueByItem, getRangePickerMiddlewareParams } from './rangePicker/utils';
import { normalizeRegionSelectValueByItem, getRegionSelectMiddlewareParams } from './regionSelect/utils';
import { getGameEngineValue } from './gameEngine/utils';
import { getGameSelectValue } from './gameSelect/utils';
import { getGenreSelectMiddlewareParams, normalizeGenreSelectValueByItem } from './genreSelect/utils';
import { normalizeMultipleSelectValueByItem } from './multipleSelect/utils';
import { normalizePublisherClassSelectValueByItem } from './publisherClassSelect/utils';
import { normalizeRevenueGrowthFilterValue } from './revenueGrowth/utils';

import type { IFilterControlAdapter } from './controlModule';
import type { IFilterItemConfig } from '@intelligence/v3/filterCore/types';

const normalizeStringArrayValueByItem = (item: IFilterItemConfig, value: unknown): string[] => {
  return normalizeStringArrayByItemOptions(item, value);
};

const normalizeGameEngineValueByItem = (item: IFilterItemConfig, value: unknown): IRelationalValue => {
  const engineValue = getGameEngineValue(value);
  const normalizedValues = normalizeStringArrayValueByItem(item, engineValue.value);
  const logic = engineValue.logic === 'AND' || engineValue.logic === 'OR' ? engineValue.logic : 'OR';

  return {
    logic,
    value: normalizedValues,
  };
};

export const FILTER_CONTROL_ADAPTERS: IFilterControlAdapter[] = [
  {
    ...rangePickerControl,
    middlewareUtils: new RangePickerMiddlewareUtils(),
    defaultFieldMap: {
      api: { granularity: 'granularity', startDate: 'start_date', endDate: 'end_date' },
      url: { granularity: 'granularity', startDate: 'startDate', endDate: 'endDate' },
    },
    getMiddlewareParams: getRangePickerMiddlewareParams,
    normalizeValue: normalizeRangePickerValueByItem,
  },
  {
    ...regionSelectControl,
    middlewareUtils: new RegionSelectMiddlewareUtils(),
    defaultFieldMap: {
      api: { markets: 'markets' },
      url: { markets: 'markets' },
    },
    getMiddlewareParams: getRegionSelectMiddlewareParams,
    normalizeValue: normalizeRegionSelectValueByItem,
  },
  {
    ...genreSelectControl,
    middlewareUtils: new GenreSelectMiddlewareUtils(),
    defaultFieldMap: {
      api: {
        genre: 'genre',
        subGenre: 'sub_genre',
        genreSource: 'genre_source',
        ieggGenreReq: 'iegg_genre_req',
        appannieGenre: 'appannie_genre',
        sensortowerGenre: 'sensortower_genre',
        appmagicGenre: 'appmagic_genre',
        minigameGenre: 'minigame_genre',
      },
      url: {
        genreSource: 'genreSource',
        mainGenres: 'mainGenres',
        subGenres: 'subGenres',
        level3Genres: 'level3Genres',
        level4Genres: 'level4Genres',
      },
    },
    getMiddlewareParams: getGenreSelectMiddlewareParams,
    normalizeValue: normalizeGenreSelectValueByItem,
  },
  {
    ...gameSelectControl,
    middlewareUtils: new GameSelectMiddlewareUtils(),
    defaultFieldMap: {
      api: {
        entityTypesIdsObj: 'entityTypesIdsObj',
        isSearchAll: 'isSearchAll',
        keywords: 'keywords',
        value: 'filter_ids',
      },
      url: { isSearchAll: 'isSearchAll', keywords: 'keywords', value: 'gameIds' },
    },
    normalizeValue: (_item, value) => {
      return getGameSelectValue(value);
    },
  },
  {
    ...companySelectControl,
    middlewareUtils: new CompanySelectMiddlewareUtils(),
    defaultFieldMap: {
      api: { value: 'companies' },
      url: { value: 'company' },
    },
    normalizeValue: normalizeStringArrayValueByItem,
  },
  {
    ...multipleSelectControl,
    middlewareUtils: new MultipleSelectMiddlewareUtils(),
    normalizeValue: normalizeMultipleSelectValueByItem,
  },
  {
    ...publisherClassSelectControl,
    middlewareUtils: new PublisherClassSelectMiddlewareUtils(),
    normalizeValue: normalizePublisherClassSelectValueByItem,
  },
  {
    ...tagSelectControl,
    middlewareUtils: new TagSelectMiddlewareUtils(),
    normalizeValue: normalizeStringArrayByItemOptionsKeepEmpty,
  },
  {
    ...sensorTowerThemeControl,
    middlewareUtils: new SensorTowerThemeMiddlewareUtils(),
    defaultFieldMap: {
      url: { value: 'theme' },
    },
    normalizeValue: normalizeLeafStringArrayByItemOptionsKeepEmpty,
  },
  {
    ...numberRangeControl,
    middlewareUtils: new NumberRangeFilterMiddlewareUtils(),
    normalizeValue: (_item, value) => {
      return getNumberRangeValue(value);
    },
  },
  {
    ...revenueRangeControl,
    middlewareUtils: new RevenueRangeMiddlewareUtils(),
    normalizeValue: (_item, value) => {
      return getNumberRangeValue(value);
    },
  },
  {
    ...revenueGrowthControl,
    middlewareUtils: new RevenueGrowthMiddlewareUtils(),
    defaultFieldMap: {
      api: { opt: 'revenue_growth_compare_opt', value: 'revenue_growth_compare_value' },
      url: { opt: 'revenueGrowthCompareOpt', value: 'revenueGrowthCompareValue' },
    },
    normalizeValue: (_item, value) => {
      return normalizeRevenueGrowthFilterValue(value as IComparableValue);
    },
  },
  {
    ...dataSourceControl,
    middlewareUtils: new DataSourceMiddlewareUtils(),
    defaultFieldMap: {
      api: { value: 'data_sources' },
      url: { value: 'dataSource' },
    },
    normalizeValue: normalizeStringArrayValueByItem,
  },
  {
    ...platformControl,
    middlewareUtils: new PlatformMiddlewareUtils(),
    defaultFieldMap: {
      api: { value: 'platforms' },
      url: { value: 'platform' },
    },
    normalizeValue: normalizeStringArrayValueByItem,
  },
  {
    ...gameEngineControl,
    middlewareUtils: new GameEngineMiddlewareUtils(),
    defaultFieldMap: {
      api: { logic: 'game_engine_relation', value: 'game_engine' },
      url: { logic: 'gameEngineRelation', value: 'gameEngine' },
    },
    normalizeValue: normalizeGameEngineValueByItem,
  },
];

FILTER_CONTROL_ADAPTERS.forEach((adapter) => {
  if (!adapter.getManagedUrlKeys) {
    adapter.getManagedUrlKeys = (item, params) => {
      return adapter.middlewareUtils.getManagedUrlKeys?.(item, params) || [];
    };
  }
});

const FILTER_CONTROL_ADAPTER_MAP = FILTER_CONTROL_ADAPTERS.reduce<Map<string, IFilterControlAdapter>>(
  (result, adapter) => {
    result.set(adapter.type, adapter);
    return result;
  },
  new Map<string, IFilterControlAdapter>(),
);

export const getFilterControlAdapter = (type?: string | null): IFilterControlAdapter | undefined => {
  if (!type) {
    return undefined;
  }

  return FILTER_CONTROL_ADAPTER_MAP.get(type);
};
